import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdSearch, MdRefresh, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  handleSessionExpired,
  getDefaultPageSize,
  fetchApplicationAppId,
  bigAmountFormat,
  getAuthUserCurrency,
  detectDateRangePreset,
  getDateRangeForPreset,
  DATE_RANGE_PRESETS,
  fetchAjaxListData,
  getBrowserTimeZone,
  EVENT_METRIC_OPTION,
  exportListCsv,
  labelizeKey,
} from "../../utils";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import "./engage.css";

const SOURCE_OPTIONS = [
  { value: "", label: "Select sources" },
  { value: "0", label: "Campaign" },
  { value: "1", label: "Organic" },
];

function getDefaultFilters() {
  const range = getDateRangeForPreset("last_24_hours");
  return {
    metric_id: "",
    campaign_id: "",
    app_user_id: "",
    is_organic: "",
    match_method: "",
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    datePreset: range.preset,
  };
}

function eventListHasActiveFilters(f) {
  return Boolean(
    (f.metric_id || "").trim() ||
      String(f.campaign_id || "").trim() ||
      String(f.app_user_id || "").trim() ||
      String(f.is_organic || "").trim() ||
      (f.match_method || "").trim()
  );
}

export default function EventList() {
  const { appId: routeAppId } = useParams();
  const [appUuid, setAppUuid] = useState(null);
  const loadApp = useCallback(async () => {
    const uuid = await fetchApplicationAppId(routeAppId);
    if (uuid) setAppUuid(uuid);
    return uuid;
  }, [routeAppId]);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  // App's configured Event Metrics (active only) — [] means legacy/no custom metrics, in which
  // case the Event Metric dropdown falls back to the single default EVENT_METRIC_OPTION entry.
  const [eventMetrics, setEventMetrics] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [draftFilters, setDraftFilters] = useState(getDefaultFilters);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);

  // This page is grouped by event_type already (one row per event), so the only filter it
  // needs is which configured Event Metric to scope the rows by — not a separate raw "Event"
  // dropdown (that would just collapse the table down to a single row).
  // "Event Metric" dropdown — separate filter: which configured Event Metric's event_names
  // allow-list (or the legacy revenue>0 rule, via EVENT_METRIC_OPTION) to scope the list by.
  const metricOptions = useMemo(
    () => [
      { value: "", label: "Select event metrics" },
      ...(eventMetrics.length
        ? eventMetrics.map((m) => ({
            value: m.id == null ? EVENT_METRIC_OPTION.value : String(m.id),
            label: m.label,
          }))
        : [{ value: EVENT_METRIC_OPTION.value, label: EVENT_METRIC_OPTION.label }]),
    ],
    [eventMetrics]
  );

  const fetchCampaignOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "campaigns",
        q: search,
        id: ids,
        extra: { app_id: appUuid },
        mode: "options",
      }),
    [appUuid]
  );

  const fetchUserOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "app-users",
        q: search,
        id: ids,
        extra: { app_id: appUuid },
        mode: "options",
      }),
    [appUuid]
  );

  const loadData = useCallback(
    async (nextPage = page, nextFilters = filters, uuid = appUuid) => {
      if (!uuid) return;
      setLoading(true);
      const res = await fetchWithAuth(`${Utils.baseurl}get/campaign/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_id: uuid,
          page: nextPage,
          pageSize,
          metric_id: (nextFilters.metric_id || "").trim(),
          campaign_id: nextFilters.campaign_id || undefined,
          app_user_id: nextFilters.app_user_id || undefined,
          is_organic: nextFilters.is_organic,
          match_method: (nextFilters.match_method || "").trim(),
          ...(nextFilters.dateFrom && nextFilters.dateTo
            ? {
                date_from: nextFilters.dateFrom,
                date_to: nextFilters.dateTo,
                timezone: getBrowserTimeZone() || undefined,
              }
            : {}),
        }),
      });
      const result = await res.json().catch(() => ({}));
      setLoading(false);
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Failed to load events");
        return;
      }
      setRows(result.data || []);
      setEventMetrics(Array.isArray(result.metrics) ? result.metrics : []);
      setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
    },
    [appUuid, page, filters, pageSize]
  );

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.metric_id || "").trim()) sp.set("metric_id", f.metric_id.trim());
    if (String(f.campaign_id || "").trim()) sp.set("campaign_id", String(f.campaign_id).trim());
    if (String(f.app_user_id || "").trim()) sp.set("app_user_id", String(f.app_user_id).trim());
    if (String(f.is_organic || "").trim()) sp.set("is_organic", String(f.is_organic).trim());
    if ((f.match_method || "").trim()) sp.set("match_method", f.match_method.trim());
    if (f.dateFrom) sp.set("date_from", f.dateFrom);
    if (f.dateTo) sp.set("date_to", f.dateTo);
    if (f.datePreset && f.datePreset !== "custom") sp.set("preset", f.datePreset);
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    if (!routeAppId) return;
    const hasDateParams = searchParams.has("date_from") || searchParams.has("date_to") || searchParams.has("preset");
    if (!hasDateParams) {
      writeParams(1, getDefaultFilters());
      return;
    }

    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const dateFrom = searchParams.get("date_from") || "";
    const dateTo = searchParams.get("date_to") || "";
    const presetParam = searchParams.get("preset");
    const datePreset =
      presetParam ||
      (dateFrom && dateTo ? detectDateRangePreset(dateFrom, dateTo) : "") ||
      "";
    const f = {
      metric_id: searchParams.get("metric_id") || "",
      campaign_id: searchParams.get("campaign_id") || "",
      app_user_id: searchParams.get("app_user_id") || "",
      is_organic: searchParams.get("is_organic") || "",
      match_method: searchParams.get("match_method") || "",
      dateFrom,
      dateTo,
      datePreset,
    };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(eventListHasActiveFilters(f));
    (async () => {
      const uuid = (await loadApp()) || appUuid;
      if (uuid) loadData(p, f, uuid);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAppId, searchParams]);

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(eventListHasActiveFilters(f));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const defaults = getDefaultFilters();
    setDraftFilters(defaults);
    setFilters(defaults);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, defaults);
  };

  const onRefresh = () => {
    loadData(page, filters, appUuid);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("get/campaign/events", {
        app_id: appUuid,
        metric_id: (filters.metric_id || "").trim(),
        campaign_id: filters.campaign_id || undefined,
        app_user_id: filters.app_user_id || undefined,
        is_organic: filters.is_organic,
        match_method: (filters.match_method || "").trim(),
        ...(filters.dateFrom && filters.dateTo
          ? {
              date_from: filters.dateFrom,
              date_to: filters.dateTo,
              timezone: getBrowserTimeZone() || undefined,
            }
          : {}),
      }, "events.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Events</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={onRefresh}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown
              className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
              size={22}
            />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <DateRangePicker
                      dateFrom={draftFilters.dateFrom}
                      dateTo={draftFilters.dateTo}
                      preset={draftFilters.datePreset}
                      presets={DATE_RANGE_PRESETS}
                      allowClear={false}
                      onChange={({ dateFrom, dateTo, preset }) =>
                        setDraftFilters((p) => ({
                          ...p,
                          dateFrom: dateFrom || "",
                          dateTo: dateTo || "",
                          datePreset: preset || "",
                        }))
                      }
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="event-filter-metric"
                      instanceId="event-filter-metric"
                      classNamePrefix="ns-event-filter-metric"
                      isSearchable
                      placeholder="Select event metrics"
                      options={metricOptions}
                      value={
                        metricOptions.find((o) => o.value === String(draftFilters.metric_id || "")) ||
                        metricOptions[0]
                      }
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, metric_id: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <AsyncSearchSelect
                      inputId="event-filter-campaign"
                      instanceId="event-filter-campaign"
                      classNamePrefix="ns-event-filter-campaign"
                      isClearable
                      placeholder="Select campaigns — type to search"
                      enabled={Boolean(appUuid)}
                      fetchOptions={fetchCampaignOpts}
                      value={draftFilters.campaign_id || null}
                      onChange={(opt) =>
                        setDraftFilters((p) => ({ ...p, campaign_id: opt?.value ?? "" }))
                      }
                      styles={selectStyles}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="event-filter-source"
                      instanceId="event-filter-source"
                      classNamePrefix="ns-event-filter-source"
                      isSearchable
                      placeholder="Select sources"
                      options={SOURCE_OPTIONS}
                      value={
                        SOURCE_OPTIONS.find((o) => o.value === String(draftFilters.is_organic || "")) ||
                        SOURCE_OPTIONS[0]
                      }
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, is_organic: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <AsyncSearchSelect
                      inputId="event-filter-user"
                      instanceId="event-filter-user"
                      classNamePrefix="ns-event-filter-user"
                      isClearable
                      placeholder="Select users — type to search"
                      enabled={Boolean(appUuid)}
                      fetchOptions={fetchUserOpts}
                      value={draftFilters.app_user_id || null}
                      onChange={(opt) =>
                        setDraftFilters((p) => ({ ...p, app_user_id: opt?.value ?? "" }))
                      }
                      styles={selectStyles}
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" title="Apply filters" onClick={onApplyFilters}>
                        <MdSearch />
                        <span>Search</span>
                      </button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Reset filters" onClick={onResetFilters}>
                        <MdRefresh />
                        <span>Reset</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Count</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="3" className="text-center">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center">
                          No events found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.event_type}>
                          <td>
                            <span className="engage-type-pill">{labelizeKey(r.event_type)}</span>
                          </td>
                          <td>{bigAmountFormat(r.count ?? 0)}</td>
                          <td>
                            {r.revenue != null && Number(r.revenue) !== 0
                              ? bigAmountFormat(r.revenue, getAuthUserCurrency())
                              : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total}
                pageSize={pageSize}
                totalPages={meta.totalPages}
                ariaLabel="Events pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
